/**
 * File: numbersPeriodUtils.ts
 * Path: apps/admin/src/shared/utils/numbersPeriodUtils.ts
 * Description: The periods the Numbers page compares: this month, quarter or
 * year against the one before. Days are South African calendar days, as
 * plain YYYY-MM-DD, both ends included.
 */

import { addMonthsToDate, southAfricanDay } from "@idk/config";

export const PERIOD_KINDS = ["month", "quarter", "year"] as const;
export type PeriodKind = (typeof PERIOD_KINDS)[number];

export const PERIOD_KIND_LABELS: Record<PeriodKind, string> = {
  month: "This month vs last month",
  quarter: "This quarter vs last quarter",
  year: "This year vs last year",
};

export interface Period {
  kind: PeriodKind;
  /** First day, YYYY-MM-DD. */
  from: string;
  /** Last day of the whole period, YYYY-MM-DD. */
  to: string;
  /** "October 2026", "Q4 2026", "2026". */
  label: string;
  /** Days of the period that have passed by `today` (the whole period once it is over). */
  elapsedDays: number;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_MS = 86_400_000;
const dayNumber = (iso: string): number => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;
const pad = (n: number): string => String(n).padStart(2, "0");

/** The period of `kind` that holds `today`, or the one `back` periods before it. */
export const periodFor = (kind: PeriodKind, today: string, back = 0): Period => {
  const [y, m] = today.split("-").map(Number);
  const length = kind === "month" ? 1 : kind === "quarter" ? 3 : 12;
  const startMonth = kind === "month" ? m : kind === "quarter" ? m - ((m - 1) % 3) : 1;
  const from = addMonthsToDate(`${y}-${pad(startMonth)}-01`, -length * back);
  const next = addMonthsToDate(from, length);
  const to = new Date(dayNumber(next) * DAY_MS - DAY_MS).toISOString().slice(0, 10);
  const [fy, fm] = from.split("-").map(Number);
  const label = kind === "month" ? `${MONTHS[fm - 1]} ${fy}` : kind === "quarter" ? `Q${Math.floor((fm - 1) / 3) + 1} ${fy}` : String(fy);
  const lastDay = today < to ? today : to;
  return { kind, from, to, label, elapsedDays: Math.max(0, dayNumber(lastDay) - dayNumber(from) + 1) };
};

export const dayInPeriod = (day: string, period: Pick<Period, "from" | "to">): boolean => day >= period.from && day <= period.to;

export const msInPeriod = (ms: number | null, period: Pick<Period, "from" | "to">): boolean =>
  ms !== null && dayInPeriod(southAfricanDay(ms), period);

/** The YYYY-MM of every month the period covers. */
export const monthsOf = (period: Pick<Period, "from" | "to">): string[] => {
  const out: string[] = [];
  let cursor = period.from;
  while (cursor <= period.to) {
    out.push(cursor.slice(0, 7));
    cursor = addMonthsToDate(`${cursor.slice(0, 7)}-01`, 1);
  }
  return out;
};
